/**
 * RIG contact modal — the ONLY home of excluded PII on the surface. Nothing
 * here (email, phone, LinkedIn, notes, timeline) appears on the graph or reads.
 *
 * PII is fetched live from get_contact_detail (owner/admin-gated): on 200 the
 * full record renders; on 403 (denial) and on error we keep an honest ownership
 * state; 404 (not-found) is distinguished from denial. A contact that already
 * carries `pii` (fixture/demo) renders it directly without a fetch.
 *
 * Closes on the close button, scrim click, or Escape.
 */

import { useEffect } from "react";
import type { RigContact } from "../types";
import { TEMP_LABEL, roleSuffix } from "../logic";
import { useContactDetail, type ContactPiiFields } from "../live";
import { BasisChip } from "./parts";

interface RigContactModalProps {
  contact: RigContact | null;
  onClose: () => void;
}

type PiiState = "ok" | "loading" | "forbidden" | "not_found" | "error";

export function RigContactModal({ contact, onClose }: RigContactModalProps): JSX.Element | null {
  useEffect(() => {
    if (!contact) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [contact, onClose]);

  // Fetch live PII unless the contact already carries it (fixture/demo). Hook
  // runs unconditionally; it self-disables for a non-UUID / absent id.
  const detail = useContactDetail(contact && !contact.pii ? contact.id : null);

  if (!contact) return null;
  const owner = contact.owner ?? "A colleague";
  const basicProv =
    contact.basis === "direct"
      ? `Matched as a direct tie — ${contact.phrase}`
      : `Matched as a consortium participant — ${contact.phrase}`;

  let fields: ContactPiiFields | null = null;
  let state: PiiState;
  if (contact.pii) {
    const p = contact.pii;
    fields = { email: p.email, phone: p.phone, linkedin: p.linkedin, note: p.note, timeline: p.timeline };
    state = "ok";
  } else if (detail.isPending) {
    state = "loading";
  } else if (detail.data) {
    if (detail.data.status === "ok") {
      fields = detail.data.fields;
      state = "ok";
    } else {
      state = detail.data.status;
    }
  } else {
    state = "error";
  }

  const ownershipBlock = (
    <div className="rig-msec">
      <h5>Contact record</h5>
      <div className="rig-excluded-box">
        <dl className="rig-mgrid">
          <dt>Owned by</dt>
          <dd>{owner}</dd>
        </dl>
        <div className="rig-note-body" style={{ marginTop: 10 }}>
          Full contact details (email · phone · LinkedIn · notes · timeline) open to the owner or an
          admin.
          {contact.owner ? ` Reach ${contact.owner} to coordinate outreach.` : ""}
        </div>
      </div>
    </div>
  );
  const provenanceBlock = (
    <div className="rig-msec">
      <h5>Basis provenance</h5>
      <div className="rig-prov">{basicProv}</div>
    </div>
  );

  return (
    <div
      className="sx-rig-modal-scrim rig-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sx-rig-modal" role="dialog" aria-modal="true" aria-label={contact.name}>
        <button className="rig-modal-close" onClick={onClose} aria-label="Close" type="button">
          ✕
        </button>
        <div className="rig-modal-head">
          <div className="rig-mtitle">
            {contact.name}
            {roleSuffix(contact.title, contact.company)}
          </div>
          <div className="rig-msub">
            {contact.sourceGroup} · met {contact.dateMet}
          </div>
          <div style={{ marginTop: 9 }}>
            <BasisChip basis={contact.basis} />
          </div>
        </div>
        <div className="rig-modal-body">
          <div className="rig-mband">
            {state === "ok"
              ? "Full record — opens excluded detail not shown anywhere on the graph surface"
              : "Contact record — full detail opens to the owner or an admin"}
          </div>

          <div className="rig-msec">
            <h5>Surface context (repeated for orientation)</h5>
            <dl className="rig-mgrid">
              <dt>Company</dt>
              <dd>{contact.company ?? "—"}</dd>
              <dt>Owner</dt>
              <dd>{owner}</dd>
              <dt>Source group</dt>
              <dd>{contact.sourceGroup}</dd>
              <dt>Date met</dt>
              <dd>{contact.dateMet}</dd>
              <dt>Interactions</dt>
              <dd>
                {contact.touches} touches
                {contact.lastDays != null ? `, last ${contact.lastDays}d` : ""}
              </dd>
              <dt>Temperature</dt>
              <dd>{TEMP_LABEL[contact.temp]}</dd>
            </dl>
          </div>

          {state === "ok" && fields ? (
            <>
              <div className="rig-msec">
                <h5>Excluded PII — modal only</h5>
                <div className="rig-excluded-box">
                  <dl className="rig-mgrid">
                    <dt>Email</dt>
                    <dd>{fields.email}</dd>
                    <dt>Phone</dt>
                    <dd>{fields.phone}</dd>
                    <dt>LinkedIn</dt>
                    <dd>{fields.linkedin}</dd>
                  </dl>
                  <div style={{ marginTop: 10 }}>
                    <div className="rig-note-label">Notes / interaction body</div>
                    <div className="rig-note-body">{fields.note}</div>
                  </div>
                </div>
              </div>

              {provenanceBlock}

              <div className="rig-msec">
                <h5>Interaction timeline</h5>
                {fields.timeline.length > 0 ? (
                  <ul className="rig-ledger">
                    {fields.timeline.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="rig-note-body">No recorded interactions.</div>
                )}
              </div>
            </>
          ) : state === "loading" ? (
            <div className="rig-msec">
              <h5>Contact record</h5>
              <div className="rig-excluded-box">
                <div className="rig-note-body">Loading the full contact record…</div>
              </div>
            </div>
          ) : state === "not_found" ? (
            <>
              <div className="rig-msec">
                <h5>Contact record</h5>
                <div className="rig-excluded-box">
                  <div className="rig-note-body">This contact record is no longer available.</div>
                </div>
              </div>
              {provenanceBlock}
            </>
          ) : (
            <>
              {ownershipBlock}
              {provenanceBlock}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
